import { api } from './api.js';

export type VoiceState = 'idle' | 'listening' | 'transcribing' | 'thinking' | 'speaking';

export interface VoiceCallbacks {
  onState(state: VoiceState): void;
  /** 0..1 loudness every frame: Anthony's mic while listening, Alfred's voice while speaking. */
  onLevel(level: number): void;
  /** Anthony said something: send it to Alfred and resolve with the reply text. */
  ask(text: string): Promise<string | null>;
  onError(message: string): void;
}

const SPEECH_THRESHOLD = 0.035; // RMS level that counts as talking
const END_SILENCE_MS = 1200; // pause that ends what Anthony is saying
const NO_SPEECH_TIMEOUT_MS = 8000; // nobody speaks → end the conversation
const MAX_UTTERANCE_MS = 45_000;

/**
 * A hands-free conversation while the app is open: listen until Anthony pauses, transcribe,
 * ask Alfred, speak the reply, listen again. Ends after a quiet stretch, or when stopped.
 */
export class VoiceSession {
  private stream: MediaStream | null = null;
  private ctx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private recorder: MediaRecorder | null = null;
  private player = new Audio();
  private outAnalyser: AnalyserNode | null = null;
  private active = false;
  private frame = 0;
  private state: VoiceState = 'idle';

  constructor(private readonly cb: VoiceCallbacks) {}

  get running() {
    return this.active;
  }

  /** Call from a tap: browsers only allow the mic and audio playback after a user gesture. */
  async start(): Promise<void> {
    if (this.active) return;
    this.active = true;
    try {
      this.stream ??= await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      this.ctx ??= new AudioContext();
      await this.ctx.resume();
      this.preparePlayer(this.ctx);
      const source = this.ctx.createMediaStreamSource(this.stream);
      this.analyser = this.ctx.createAnalyser();
      this.analyser.fftSize = 1024;
      source.connect(this.analyser);
      this.unlockPlayback();
      this.listen();
    } catch (err) {
      this.cb.onError(
        err instanceof DOMException && err.name === 'NotAllowedError'
          ? 'Microphone access was blocked. Allow it for Alfred in the browser settings.'
          : 'Couldn’t start the microphone.',
      );
      this.stop();
    }
  }

  /** End the conversation and release the microphone. */
  stop(): void {
    this.active = false;
    cancelAnimationFrame(this.frame);
    if (this.recorder?.state === 'recording') this.recorder.stop();
    this.recorder = null;
    this.player.pause();
    this.player.removeAttribute('src');
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    void this.ctx?.close();
    this.ctx = null;
    this.setState('idle');
    this.cb.onLevel(0);
  }

  /** While Alfred is speaking: cut him off and listen. */
  interrupt(): void {
    if (this.state !== 'speaking') return;
    this.player.pause();
    this.listen();
  }

  private setState(state: VoiceState) {
    this.state = state;
    this.cb.onState(state);
  }

  private listen() {
    if (!this.active || !this.stream || !this.analyser) return;
    cancelAnimationFrame(this.frame);
    this.setState('listening');
    const mimeType = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/webm'].find((t) =>
      MediaRecorder.isTypeSupported(t),
    );
    const recorder = new MediaRecorder(this.stream, mimeType ? { mimeType } : undefined);
    this.recorder = recorder;
    const chunks: Blob[] = [];
    recorder.ondataavailable = (e) => e.data.size > 0 && chunks.push(e.data);

    let heardSpeech = false;
    let lastSound = performance.now();
    const startedAt = performance.now();
    const samples = new Float32Array(this.analyser.fftSize);

    recorder.onstop = () => {
      cancelAnimationFrame(this.frame);
      this.cb.onLevel(0);
      if (!this.active || this.recorder !== recorder) return;
      if (!heardSpeech) return this.stop(); // a quiet stretch ends the conversation
      void this.handleUtterance(new Blob(chunks, { type: recorder.mimeType || 'audio/webm' }));
    };

    const tick = () => {
      if (!this.analyser || recorder.state !== 'recording') return;
      this.analyser.getFloatTimeDomainData(samples);
      let sum = 0;
      for (const s of samples) sum += s * s;
      const rms = Math.sqrt(sum / samples.length);
      this.cb.onLevel(Math.min(1, rms * 8));
      const now = performance.now();
      if (rms > SPEECH_THRESHOLD) {
        heardSpeech = true;
        lastSound = now;
      }
      const done =
        (heardSpeech && now - lastSound > END_SILENCE_MS) ||
        (!heardSpeech && now - startedAt > NO_SPEECH_TIMEOUT_MS) ||
        now - startedAt > MAX_UTTERANCE_MS;
      if (done) recorder.stop();
      else this.frame = requestAnimationFrame(tick);
    };
    recorder.start(250);
    this.frame = requestAnimationFrame(tick);
  }

  private async handleUtterance(audio: Blob) {
    try {
      this.setState('transcribing');
      const { text } = await api.transcribe(audio);
      if (!this.active) return;
      if (!text) return this.listen(); // nothing intelligible: just keep listening

      this.setState('thinking');
      const reply = await this.cb.ask(text);
      if (!this.active || !reply) return reply ? undefined : this.listen();

      this.setState('speaking');
      const { id } = await api.speak(reply);
      if (!this.active) return;
      this.player.src = `/api/voice/audio/${id}`;
      this.player.onended = () => this.active && this.listen();
      this.player.onerror = () => this.active && this.listen();
      await this.player.play();
      this.meterOutput();
    } catch (err) {
      this.cb.onError(err instanceof Error ? err.message : String(err));
      if (this.active) this.listen();
    }
  }

  /**
   * A fresh player per conversation (an element can only be wired into one AudioContext),
   * routed through an analyser so the HUD can pulse with Alfred's voice. iOS Safari has a
   * history of silencing media routed through Web Audio, so there it plays directly and the
   * HUD gets a synthetic speaking rhythm instead.
   */
  private preparePlayer(ctx: AudioContext) {
    this.player = new Audio();
    this.player.preload = 'auto';
    this.outAnalyser = null;
    if (isIOS()) return;
    try {
      const source = ctx.createMediaElementSource(this.player);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      analyser.connect(ctx.destination);
      this.outAnalyser = analyser;
    } catch {
      // Plays normally, just without a measured level.
    }
  }

  /** Reports Alfred's loudness while he speaks. */
  private meterOutput() {
    const analyser = this.outAnalyser;
    const samples = analyser ? new Float32Array(analyser.fftSize) : null;
    const started = performance.now();
    const tick = () => {
      if (!this.active || this.state !== 'speaking' || this.player.paused) {
        this.cb.onLevel(0);
        return;
      }
      let level: number;
      if (analyser && samples) {
        analyser.getFloatTimeDomainData(samples);
        let sum = 0;
        for (const s of samples) sum += s * s;
        level = Math.min(1, Math.sqrt(sum / samples.length) * 5);
      } else {
        // Syllable-ish rhythm: two beating waves, never fully silent.
        const t = (performance.now() - started) / 1000;
        level = 0.25 + 0.2 * Math.abs(Math.sin(t * 7.3)) * (0.6 + 0.4 * Math.sin(t * 2.1));
      }
      this.cb.onLevel(level);
      this.frame = requestAnimationFrame(tick);
    };
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(tick);
  }

  /**
   * iOS only lets audio play from code that runs in a tap. Playing a moment of silence now,
   * inside the tap that started the conversation, unlocks the player for Alfred's replies.
   */
  private unlockPlayback() {
    const ctx = this.ctx;
    if (!ctx) return;
    const buffer = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * 0.05), ctx.sampleRate);
    const dest = ctx.createMediaStreamDestination();
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(dest);
    src.start();
    this.player.srcObject = dest.stream;
    void this.player.play().catch(() => {});
    this.player.srcObject = null;
  }
}

function isIOS() {
  return (
    /iP(hone|ad|od)/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}
